import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { BookOpen, ChevronLeft, ChevronRight, MessageCircleQuestion, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HighlightScope, HighlightText, useFlashHighlight } from "@/components/highlights/HighlightScope";
import { useHighlights } from "@/components/highlights/useHighlights";
import { bibleLabel } from "@/lib/highlights";
import { useAuth } from "@/context/AuthContext";

export const READER_KEY = "qg-reader-open";
export const TRANSLATION_KEY = "qg-reader-translation";
const DEFAULT_TRANSLATIONS = ["KJV"];

/* Bible versions the current viewer may read (admin-controlled; KJV always). */
export function useBibleVersions() {
  const { user, loading } = useAuth();
  return useQuery({ queryKey: ["bible-books", user?.user_id || "anon"], queryFn: async () => (await api.get("/bible/books")).data, enabled: !loading, staleTime: 60 * 60 * 1000 });
}

function Verse({ v, current, activeId, onPick, scrollRef, onVerseClick }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!current) return undefined;
    const t = setTimeout(() => {
      const box = scrollRef.current, el = ref.current;
      if (!el) return;
      if (box && box.scrollHeight > box.clientHeight + 4) box.scrollTo({ top: el.offsetTop - box.clientHeight / 2 + el.clientHeight / 2, behavior: "smooth" });
      else el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 150);
    return () => clearTimeout(t);
  }, [current, scrollRef]);
  const has = v.questions.length > 0;
  const active = v.questions.some((q) => q.id === activeId);
  const Icon = (
    <MessageCircleQuestion className="h-[18px] w-[18px] text-gold" strokeWidth={2.25} />
  );
  return (
    <div
      ref={ref}
      id={`reader-verse-${v.verse}`}
      data-testid={`reader-verse-${v.verse}`}
      onClick={(e) => { if (onVerseClick && window.getSelection()?.isCollapsed && !e.target.closest("button, a, [role=dialog]")) onVerseClick(v.verse); }}
      className={`group/verse relative flex items-start gap-2 rounded-xl px-3 py-2 transition-colors ${current ? "bg-gold/15 ring-1 ring-gold/50" : has ? "hover:bg-muted/60" : onVerseClick ? "hover:bg-muted/40" : ""}`}
    >
      <p className="min-w-0 flex-1 font-serif text-[17px] leading-relaxed text-foreground/90">
        <sup className={`mr-1.5 font-mono text-[10px] ${current ? "text-gold" : "text-muted-foreground"}`}>{v.verse}</sup>
        <HighlightText k={String(v.verse)} text={v.text} />
      </p>
      {has && v.questions.length === 1 && (
        <button
          type="button"
          onClick={() => onPick(v.questions[0].id)}
          title={v.questions[0].question_kjv}
          aria-label={`Open question: ${v.questions[0].question_kjv}`}
          data-testid={`reader-question-${v.questions[0].id}`}
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all hover:scale-110 hover:border-gold hover:bg-gold/25 ${active ? "border-gold bg-gold/25 shadow-sm" : "border-gold/40 bg-gold/10"}`}
        >
          {Icon}
        </button>
      )}
      {has && v.questions.length > 1 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title={`${v.questions.length} questions in this verse`}
              aria-label={`${v.questions.length} questions in this verse`}
              data-testid={`reader-verse-${v.verse}-questions`}
              className={`mt-0.5 flex h-8 shrink-0 items-center gap-1 rounded-full border px-2 font-mono text-[11px] font-semibold text-gold transition-all hover:scale-110 hover:border-gold hover:bg-gold/25 ${active ? "border-gold bg-gold/25 shadow-sm" : "border-gold/40 bg-gold/10"}`}
            >
              {Icon}{v.questions.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 rounded-2xl p-1.5">
            {v.questions.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => onPick(q.id)}
                data-testid={`reader-question-${q.id}`}
                className={`block w-full rounded-xl px-3 py-2 text-left font-serif text-sm leading-snug transition-colors hover:bg-muted ${q.id === activeId ? "text-gold" : "text-foreground"}`}
              >
                {q.question_kjv}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export default function BibleReader({ book, chapter, verse, verseChapter, activeId, onClose, onChapter, standalone = false, picker = null, scroll = false, onVerseClick = null, flashId = null }) {
  const navigate = useNavigate();
  const hl = useHighlights(book, chapter);
  const { data: versions } = useBibleVersions();
  const TRANSLATIONS = versions?.translations || DEFAULT_TRANSLATIONS;
  const [translation, setTranslation] = useState(() => localStorage.getItem(TRANSLATION_KEY) || "KJV");
  useEffect(() => { localStorage.setItem(TRANSLATION_KEY, translation); }, [translation]);
  useEffect(() => { if (versions && !TRANSLATIONS.includes(translation)) setTranslation("KJV"); }, [versions, TRANSLATIONS, translation]);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["bible", book, chapter, translation],
    queryFn: async () => (await api.get(`/bible/${encodeURIComponent(book)}/${chapter}`, { params: { translation } })).data,
    enabled: !!book && !!chapter,
    staleTime: 10 * 60 * 1000,
  });
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [book, chapter]);
  useFlashHighlight(flashId, !!data && hl.all.length > 0);
  const verseKeys = (data?.verses || []).map((v) => String(v.verse));
  const bibleHl = hl.all.filter((h) => h.source === "bible" && (h.translation || "KJV") === translation);
  const mention = (start, end) => ({ kind: "verse", id: bibleLabel(book, chapter, start.key, end.key), label: bibleLabel(book, chapter, start.key, end.key), href: `/read/${book}/${chapter}?v=${start.key}` });

  return (
    <aside className={`flex h-full flex-col rounded-2xl border border-border bg-card ${standalone ? "" : "shadow-xl"}`} data-testid="bible-reader" data-lenis-prevent={standalone && !scroll ? undefined : true}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-3 sm:px-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <BookOpen className="hidden h-4 w-4 text-gold sm:block" />
          {TRANSLATIONS.length > 1 && <div className="flex shrink-0 overflow-hidden rounded-full border border-border bg-background font-mono text-[10px] uppercase tracking-wider" data-testid="reader-translation-toggle">
            {TRANSLATIONS.map((t) => (
              <button key={t} type="button" onClick={() => setTranslation(t)} aria-pressed={translation === t} data-testid={`reader-translation-${t.toLowerCase()}`}
                className={`px-2 py-1.5 transition-colors ${translation === t ? "bg-gold text-white" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
            ))}
          </div>}
          {picker || <span className="font-serif text-lg font-semibold text-foreground" data-testid="reader-title">{book} {chapter}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button type="button" disabled={!data?.prev} onClick={() => onChapter(data.prev)} aria-label="Previous chapter" data-testid="reader-prev-chapter"
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" disabled={!data?.next} onClick={() => onChapter(data.next)} aria-label="Next chapter" data-testid="reader-next-chapter"
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30">
            <ChevronRight className="h-4 w-4" />
          </button>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close reader" data-testid="reader-close"
              className="ml-1 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>
      <div ref={scrollRef} className={`relative min-h-0 flex-1 px-2 py-3 ${standalone && !scroll ? "" : "overflow-y-auto [scrollbar-width:thin]"}`}>
        {isLoading && <div className="space-y-3 p-3">{Array.from({ length: 8 }).map((_, i) => <div key={`skeleton-${i}`} className="h-5 animate-pulse rounded bg-muted" />)}</div>}
        {isError && <p className="p-4 text-sm text-muted-foreground">Couldn&apos;t load this chapter.</p>}
        <HighlightScope scope={{ source: "bible", book, chapter: Number(chapter), translation }} keys={verseKeys} highlights={bibleHl} actions={hl} mention={mention} testId="reader-verses">
          {data?.verses.map((v) => (
            <Verse key={v.verse} v={v} current={v.verse === verse && verseChapter === data.chapter} activeId={activeId} onPick={(id) => navigate(`/question/${id}`, { state: standalone ? { openReader: true } : undefined })} scrollRef={scrollRef} onVerseClick={onVerseClick} />
          ))}
        </HighlightScope>
      </div>
      <footer className="border-t border-border px-4 py-2">
        <div className="flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>Tap <MessageCircleQuestion className="inline h-3 w-3 text-gold" /> for the question · select text to highlight</span>
          <Link to={`/book/${encodeURIComponent(book)}`} className="shrink-0 hover:text-foreground" data-testid="reader-book-link">All {book} questions</Link>
        </div>
        {data?.notice && <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground" data-testid="reader-notice">{data.notice}</p>}
      </footer>
    </aside>
  );
}
